import { supabase } from '@/integrations/supabase/client';
import type { 
  Message, 
  MessageRow, 
  MessageInsert, 
  ChatParticipant,
  ChatTransformUtils 
} from '../types';
import { CHAT_CONFIG } from '../constants';

// Transform utilities for converting between DB and frontend models
export const chatTransforms: ChatTransformUtils = {
  messageFromDB: (row: MessageRow): Message => ({
    id: row.id,
    senderUserId: row.sender_user_id || '',
    receiverUserId: row.receiver_user_id || '',
    content: row.content,
    createdAt: new Date(row.created_at || ''),
    readAt: row.read_at ? new Date(row.read_at) : undefined,
  }),

  messageToDB: (message: Partial<Message>): MessageInsert => ({
    user_id: message.senderUserId!, // For backward compatibility
    sender_user_id: message.senderUserId!,
    receiver_user_id: message.receiverUserId!,
    content: message.content!,
    created_at: message.createdAt?.toISOString(),
    read_at: message.readAt?.toISOString(),
  }),

  participantFromProfile: (profile: {
    id: string;
    full_name?: string;
    username?: string;
    avatar_url?: string
  }): ChatParticipant => ({
    id: profile.id,
    fullName: profile.full_name,
    username: profile.username,
    avatarUrl: profile.avatar_url,
    unreadCount: 0, // Will be calculated separately
  }),
};

/**
 * Fetch messages between two users
 */
export const fetchMessages = async (
  currentUserId: string, 
  otherUserId: string
): Promise<Message[]> => {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .or(`and(sender_user_id.eq.${currentUserId},receiver_user_id.eq.${otherUserId}),and(sender_user_id.eq.${otherUserId},receiver_user_id.eq.${currentUserId})`)
    .order('created_at', { ascending: true })
    .limit(CHAT_CONFIG.INITIAL_MESSAGE_LIMIT);

  if (error) {
    console.error('Error fetching messages:', error);
    throw new Error(`Failed to fetch messages: ${error.message}`);
  }

  return (data || []).map(chatTransforms.messageFromDB);
};

/**
 * Send a new message
 */
export const sendMessage = async (
  senderId: string,
  receiverId: string,
  content: string
): Promise<Message> => {
  const messageData: MessageInsert = {
    user_id: senderId, // For backward compatibility
    sender_user_id: senderId,
    receiver_user_id: receiverId,
    content: content.trim(),
  };

  const { data, error } = await supabase
    .from('messages')
    .insert(messageData)
    .select()
    .single();

  if (error) {
    console.error('Error sending message:', error);
    throw new Error(`Failed to send message: ${error.message}`);
  }

  return chatTransforms.messageFromDB(data);
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (
  currentUserId: string,
  senderId: string
): Promise<void> => {
  const { error } = await supabase
    .from('messages')
    .update({ read_at: new Date().toISOString() })
    .eq('receiver_user_id', currentUserId)
    .eq('sender_user_id', senderId)
    .is('read_at', null);

  if (error) {
    console.error('Error marking messages as read:', error);
    throw new Error(`Failed to mark messages as read: ${error.message}`);
  }
};

/**
 * Get unread message count for a user
 */
export const getUnreadMessageCount = async (userId: string): Promise<number> => {
  // Check if we're online before making the request
  if (!navigator.onLine) {
    console.log('Offline: Skipping unread message count fetch');
    return 0; // Return 0 when offline instead of throwing an error
  }

  const { count, error } = await supabase
    .from('messages')
    .select('*', { count: 'exact', head: true })
    .eq('receiver_user_id', userId)
    .is('read_at', null);

  if (error) {
    console.error('Error getting unread count:', error);
    throw new Error(`Failed to get unread count: ${error.message}`);
  }

  return count || 0;
};

/**
 * Get chat participants for admin users (all users they've had conversations with)
 */
export const getAdminChatParticipants = async (adminUserId: string): Promise<ChatParticipant[]> => {
  // Get all unique user IDs that the admin has had conversations with
  // This includes both users who sent messages to admin AND users admin sent messages to
  const { data: messageData, error: messageError } = await supabase
    .from('messages')
    .select('sender_user_id, receiver_user_id, created_at')
    .or(`sender_user_id.eq.${adminUserId},receiver_user_id.eq.${adminUserId}`)
    .order('created_at', { ascending: false });

  if (messageError) {
    console.error('Error fetching admin participants:', messageError);
    throw new Error(`Failed to fetch admin participants: ${messageError.message}`);
  }

  // Get unique user IDs (excluding the admin) and their last activity
  const userActivityMap = new Map<string, Date>();
  messageData?.forEach((m: { sender_user_id: string; receiver_user_id: string; created_at: string }) => {
    const otherUserId = m.sender_user_id === adminUserId ? m.receiver_user_id : m.sender_user_id;
    if (otherUserId !== adminUserId && !userActivityMap.has(otherUserId)) {
      userActivityMap.set(otherUserId, new Date(m.created_at));
    }
  });

  const uniqueUserIds = Array.from(userActivityMap.keys());

  if (uniqueUserIds.length === 0) {
    return [];
  }

  // Fetch profile information for these users
  const { data: profiles, error: profileError } = await supabase
    .from('profiles')
    .select('id, full_name, avatar_url, username')
    .in('id', uniqueUserIds);

  if (profileError) {
    console.error('Error fetching profiles:', profileError);
    throw new Error(`Failed to fetch profiles: ${profileError.message}`);
  }

  // Transform to ChatParticipant objects with last activity
  const participants = (profiles || []).map((profile: { id: string; full_name?: string; username?: string; avatar_url?: string }) => {
    const participant = chatTransforms.participantFromProfile(profile);
    participant.lastActivity = userActivityMap.get(profile.id);
    return participant;
  });

  // Get unread counts for each participant (messages sent TO the admin that are unread)
  for (const participant of participants) {
    const { count } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('receiver_user_id', adminUserId)
      .eq('sender_user_id', participant.id)
      .is('read_at', null);

    participant.unreadCount = count || 0;
  }

  // For admin users, show all conversations regardless of read status
  // This allows admins to see and manage all chat history
  return participants;
};

/**
 * Get all users for admin user picker (excluding the current admin)
 */
export const getAllUsersForPicker = async (currentUserId: string): Promise<ChatParticipant[]> => {
  const { data: profiles, error } = await supabase
    .from('profiles')
    .select('id, full_name, avatar_url, username')
    .neq('id', currentUserId)
    .order('full_name', { ascending: true });

  if (error) {
    console.error('Error fetching users for picker:', error);
    throw new Error(`Failed to fetch users: ${error.message}`);
  }

  // Transform to ChatParticipant objects
  return (profiles || []).map((profile: { id: string; full_name?: string; username?: string; avatar_url?: string }) =>
    chatTransforms.participantFromProfile(profile)
  );
};

/**
 * Get chat participants (users who have sent messages to current user)
 */
export const getChatParticipants = async (currentUserId: string): Promise<ChatParticipant[]> => {
  // Get unique sender IDs who have sent messages to current user
  const { data: messageData, error: messageError } = await supabase
    .from('messages')
    .select('sender_user_id, created_at')
    .eq('receiver_user_id', currentUserId)
    .order('created_at', { ascending: false });

  if (messageError) {
    console.error('Error fetching participants:', messageError);
    throw new Error(`Failed to fetch participants: ${messageError.message}`);
  }

  // Get unique sender IDs and their last activity
  const senderActivityMap = new Map<string, Date>();
  messageData?.forEach((m: { sender_user_id: string; created_at: string }) => {
    if (!senderActivityMap.has(m.sender_user_id)) {
      senderActivityMap.set(m.sender_user_id, new Date(m.created_at));
    }
  });

  const uniqueSenderIds = Array.from(senderActivityMap.keys());

  if (uniqueSenderIds.length === 0) {
    return [];
  }

  // Fetch profile information for these users
  const { data: profiles, error: profileError } = await supabase
    .from('profiles')
    .select('id, full_name, avatar_url, username')
    .in('id', uniqueSenderIds);

  if (profileError) {
    console.error('Error fetching profiles:', profileError);
    throw new Error(`Failed to fetch profiles: ${profileError.message}`);
  }

  // Transform to ChatParticipant objects with last activity
  const participants = (profiles || []).map((profile: { id: string; full_name?: string; username?: string; avatar_url?: string }) => {
    const participant = chatTransforms.participantFromProfile(profile);
    participant.lastActivity = senderActivityMap.get(profile.id);
    return participant;
  });

  // Get unread counts for each participant
  for (const participant of participants) {
    const { count } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('receiver_user_id', currentUserId)
      .eq('sender_user_id', participant.id)
      .is('read_at', null);

    participant.unreadCount = count || 0;
  }

  return participants;
};

/**
 * Subscribe to realtime message updates
 */
export const subscribeToMessages = (
  currentUserId: string,
  otherUserId: string,
  onMessage: (message: Message) => void,
  onError?: (error: Error) => void
) => {
  const channel = supabase
    .channel(`${CHAT_CONFIG.REALTIME_CHANNEL_PREFIX}_${currentUserId}_${otherUserId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `or(and(sender_user_id.eq.${currentUserId},receiver_user_id.eq.${otherUserId}),and(sender_user_id.eq.${otherUserId},receiver_user_id.eq.${currentUserId}))`,
      },
      (payload: { new: MessageRow }) => {
        try {
          const message = chatTransforms.messageFromDB(payload.new);
          onMessage(message);
        } catch (error) {
          console.error('Error processing realtime message:', error);
          onError?.(error as Error);
        }
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'messages',
        filter: `or(and(sender_user_id.eq.${currentUserId},receiver_user_id.eq.${otherUserId}),and(sender_user_id.eq.${otherUserId},receiver_user_id.eq.${currentUserId}))`,
      },
      (payload: { new: MessageRow }) => {
        try {
          const message = chatTransforms.messageFromDB(payload.new);
          onMessage(message);
        } catch (error) {
          console.error('Error processing realtime message update:', error);
          onError?.(error as Error);
        }
      }
    )
    .subscribe();

  return {
    channel: channel.topic,
    subscription: channel,
    cleanup: () => {
      supabase.removeChannel(channel);
    },
  };
};

/**
 * Clear conversation - delete all messages in the conversation (clear chat history)
 */
export const clearConversation = async (
  currentUserId: string,
  otherUserId: string
): Promise<void> => {
  const { error } = await supabase
    .from('messages')
    .delete()
    .or(`and(sender_user_id.eq.${currentUserId},receiver_user_id.eq.${otherUserId}),and(sender_user_id.eq.${otherUserId},receiver_user_id.eq.${currentUserId})`);

  if (error) {
    console.error('Error clearing conversation:', error);
    throw new Error(`Failed to clear conversation: ${error.message}`);
  }
};

/**
 * Delete conversation - remove from admin's participant list by marking all messages as read
 * This keeps the messages but removes the conversation from the admin's active list
 */
export const deleteConversation = async (
  currentUserId: string,
  otherUserId: string
): Promise<void> => {
  // Mark all messages as read to remove from admin's active conversation list
  await markMessagesAsRead(currentUserId, otherUserId);

  // Also mark messages sent by current user as read (for completeness)
  const { error } = await supabase
    .from('messages')
    .update({ read_at: new Date().toISOString() })
    .eq('sender_user_id', currentUserId)
    .eq('receiver_user_id', otherUserId)
    .is('read_at', null);

  if (error) {
    console.error('Error deleting conversation from list:', error);
    throw new Error(`Failed to delete conversation from list: ${error.message}`);
  }
};

/**
 * Subscribe to unread count updates
 */
export const subscribeToUnreadCount = (
  userId: string,
  onCountChange: (count: number) => void,
  onError?: (error: Error) => void
) => {
  const channel = supabase
    .channel(`unread_count_${userId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'messages',
        filter: `receiver_user_id.eq.${userId}`,
      },
      async () => {
        try {
          const count = await getUnreadMessageCount(userId);
          onCountChange(count);
        } catch (error) {
          console.error('Error updating unread count:', error);
          onError?.(error as Error);
        }
      }
    )
    .subscribe();

  return {
    channel: channel.topic,
    subscription: channel,
    cleanup: () => {
      supabase.removeChannel(channel);
    },
  };
};
